// SCR-STAFF-408 — پروفایل مالی مراجعه‌کننده (US-BIL-090، 091، 093، 100؛ US-BIL-052): مانده و بدهی، کیف پول (نقدی و هدیه) با دفتر تراکنش‌ها، شارژ کیف پول
// (شیفت باز)، پکیج‌ها (AP-BIL-03)، اعتبارهای قابل استرداد با «استرداد» یا «انتقال به کیف پول»، مطالبات، بیعانه‌ها، پرداخت‌ها و استردادها.
// AP-BIL-04: عضویت‌ها (US-BIL-075)، صورتحساب‌های دارای مانده با «برنامه اقساط» (US-BIL-101)، «ثبت چک» (US-BIL-103) و «سوخت بدهی» (US-BIL-104)، و
// تاریخچه سوخت‌ها؛ پیوند به اقساط و چک‌های مراجعه‌کننده.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type BilCreditView, type BilInvoiceListItem, type BilPaymentMethod } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, FormDialog, JalaliDate, KeyValueList, LoadingState, PageHeader } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { useApproval } from '../iam/useApproval';
import { parseRials } from './CashierPage';
import { ChequeDialog, InstallmentPlanDialog, WriteOffDialog } from './FinanceDialogs';
import { MembershipsPanel } from './MembershipsPanel';
import { PatientPackagesPanel } from './PackagesPage';
import {
  Amount,
  bilKeys,
  cashierPatientPath,
  chequesPath,
  DepositStatusChip,
  installmentsPath,
  InvoiceStatusChip,
  needsReference,
  RefundStatusChip,
  rial,
  SCR_STAFF_408,
} from './common';
import { useConnection } from '../../offline/OfflineProvider';

const REFUND_METHODS: readonly BilPaymentMethod[] = ['Cash', 'CardPos', 'BankTransfer', 'Wallet'];
const TOPUP_METHODS: readonly BilPaymentMethod[] = ['Cash', 'CardPos', 'BankTransfer'];

export function PatientFinancePage() {
  const { t } = useTranslation('bil');
  const { id: patientId = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const connection = useConnection();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const { requireApproval, dialog: approvalDialog } = useApproval();
  const [refunding, setRefunding] = useState<BilCreditView | null>(null);
  const [refund, setRefund] = useState({ amount: '', method: '' as BilPaymentMethod | '', iban: '', reason: '', reference: '' });
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUp, setTopUp] = useState({ amount: '', method: 'Cash' as BilPaymentMethod, reference: '' });
  const [invoiceAction, setInvoiceAction] = useState<{ invoice: BilInvoiceListItem; kind: 'plan' | 'cheque' | 'write-off' } | null>(null);
  const invoices = useQuery({
    queryKey: bilKeys.invoices(session?.tenantId, `patient:${patientId}`),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/invoices', { params: { query: { patientId, limit: 100 } } })),
    enabled: patientId !== '',
  });
  const writeOffs = useQuery({
    queryKey: bilKeys.writeOffs(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/write-offs', { params: { query: { patientId } } })),
    enabled: patientId !== '',
  });
  const finance = useQuery({
    queryKey: bilKeys.finance(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/patients/{patientId}/finance', { params: { path: { patientId } } })),
    enabled: patientId !== '',
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
  const createRefund = useMutation({
    mutationFn: async (credit: BilCreditView) => {
      const body = {
        patientId,
        creditId: credit.id,
        amount: rial(parseRials(refund.amount)),
        method: refund.method || null,
        reason: refund.reason.trim() || null,
        iban: refund.iban.trim() || null,
        approvalId: null as string | null,
        reference: refund.reference.trim() || null,
      };
      const send = () => unwrap(tenantApi.POST('/api/v1/billing/refunds', { body, headers: { 'Idempotency-Key': key.current() } }));
      try {
        return await send();
      } catch (error) {
        if (isApiError(error) && error.code === 'PHX-BIL-0209' && typeof error.extensions['key'] === 'string') {
          key.settle(error);
          const outcome = await requireApproval({ key: error.extensions['key'] as string, amount: body.amount.amount, context: { patientId } });
          if (outcome.status === 'approved' || outcome.status === 'requested') {
            body.approvalId = outcome.approvalId;
            return send();
          }
        }
        throw error;
      }
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setRefunding(null);
      refresh();
    },
  });
  const toWallet = useMutation({
    mutationFn: (credit: BilCreditView) =>
      unwrap(tenantApi.POST('/api/v1/billing/credits/{id}:to-wallet', { params: { path: { id: credit.id } }, body: { amount: null }, headers: { 'Idempotency-Key': key.current() } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: refresh,
  });
  const doTopUp = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/wallets/{patientId}:top-up', {
          params: { path: { patientId } },
          body: { payments: [{ method: topUp.method, amount: rial(parseRials(topUp.amount)), reference: topUp.reference.trim() || null, cardLast4: null }] },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setTopUpOpen(false);
      setTopUp({ amount: '', method: 'Cash', reference: '' });
      refresh();
    },
  });

  if (finance.isPending) {
    return <LoadingState rows={6} />;
  }
  if (finance.error || !finance.data) {
    return <ErrorState error={finance.error} onRetry={() => void finance.refetch()} />;
  }
  const data = finance.data;
  const full = access.accessMode === 'Full';
  // BR-BIL-055 (AP-OFF-01): استرداد در حالت آفلاین غیرفعال است.
  const canRefund = full && access.can('bil.refund.create') && connection.status === 'online';
  const refundOffline = full && access.can('bil.refund.create') && connection.status === 'offline';

  return (
    <Stack data-screen-id={SCR_STAFF_408} spacing={2}>
      <PageHeader
        titleKey="bil:finance.title"
        badges={<Typography color="text.secondary">{data.patientName ?? ''}</Typography>}
        actions={
          <Stack direction="row" spacing={1}>
            {access.can('bil.payment.receive') ? (
              <Button component={RouterLink} to={cashierPatientPath(patientId)} variant="contained" data-testid="bil-finance-cashier">
                {t('bil:finance.receive')}
              </Button>
            ) : null}
            <Button component={RouterLink} to={installmentsPath(patientId)} variant="text" data-testid="bil-finance-installments">
              {t('bil:menu.installments')}
            </Button>
            <Button component={RouterLink} to={chequesPath(patientId)} variant="text" data-testid="bil-finance-cheques">
              {t('bil:menu.cheques')}
            </Button>
            {full && access.can('bil.wallet.topup') ? (
              <Button variant="outlined" onClick={() => setTopUpOpen(true)} data-testid="bil-finance-topup">
                {t('bil:finance.top_up')}
              </Button>
            ) : null}
          </Stack>
        }
      />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <KeyValueList
          columns={3}
          items={[
            { labelKey: 'bil:cashier.debt', value: <Amount money={data.debt} testId="bil-finance-debt" /> },
            { labelKey: 'bil:finance.wallet_cash', value: <Amount money={data.wallet.cash} testId="bil-finance-wallet-cash" /> },
            { labelKey: 'bil:finance.wallet_gift', value: <Amount money={data.wallet.gift} testId="bil-finance-wallet-gift" /> },
            { labelKey: 'bil:cashier.credit', value: <Amount money={data.refundableCredit} testId="bil-finance-credit" /> },
          ]}
        />
      </Paper>

      {doTopUp.data?.giftCredit && doTopUp.data.giftCredit.amount > 0 ? (
        <Alert severity="success" data-testid="bil-topup-gift">
          {t('bil:promotions.granted', { name: doTopUp.data.promotionName ?? '' })} <Amount money={doTopUp.data.giftCredit} />
        </Alert>
      ) : null}

      {/* AP-BIL-03: پکیج‌های مراجعه‌کننده با فروش، انتقال، تمدید و لغو (SCR-STAFF-406 درون پروفایل مالی). */}
      <PatientPackagesPanel patientId={patientId} />

      {/* AP-BIL-04: عضویت‌ها (SCR-STAFF-406 درون پروفایل مالی). */}
      <MembershipsPanel patientId={patientId} />

      <Section title={t('bil:finance.open_invoices')}>
        {(invoices.data?.items ?? [])
          .filter((invoice) => invoice.balance.amount > 0 && invoice.status !== 'Voided')
          .map((invoice) => (
            <TableRow key={invoice.id} data-testid="bil-finance-invoice" data-invoice-id={invoice.id}>
              <TableCell>
                <JalaliDate value={invoice.businessDate} />
              </TableCell>
              <TableCell>
                <bdi dir="ltr">{invoice.number}</bdi>
              </TableCell>
              <TableCell>
                <InvoiceStatusChip status={invoice.status} />
              </TableCell>
              <TableCell>
                <Amount money={invoice.balance} />
              </TableCell>
              <TableCell>
                {full ? (
                  <Stack direction="row" spacing={1}>
                    {access.can('bil.installment.manage') ? (
                      <Button size="small" onClick={() => setInvoiceAction({ invoice, kind: 'plan' })} data-testid="bil-finance-plan">
                        {t('bil:installments.new')}
                      </Button>
                    ) : null}
                    {access.can('bil.cheque.manage') ? (
                      <Button size="small" onClick={() => setInvoiceAction({ invoice, kind: 'cheque' })} data-testid="bil-finance-cheque">
                        {t('bil:cheques.register')}
                      </Button>
                    ) : null}
                    {access.can('bil.debt.write_off') ? (
                      <Button size="small" color="error" onClick={() => setInvoiceAction({ invoice, kind: 'write-off' })} data-testid="bil-finance-write-off">
                        {t('bil:write_off.title')}
                      </Button>
                    ) : null}
                  </Stack>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
      </Section>

      <Section title={t('bil:finance.credits')}>
        {data.credits.map((credit) => (
          <TableRow key={credit.id} data-testid="bil-credit-row">
            <TableCell>
              <JalaliDate value={credit.createdAt} />
            </TableCell>
            <TableCell>{t(`bil:credit_source.${credit.source}`)}</TableCell>
            <TableCell>{credit.originalMethod ? t(`bil:method.${credit.originalMethod}`) : '—'}</TableCell>
            <TableCell>
              <Amount money={credit.available} />
            </TableCell>
            <TableCell>
              {refundOffline && credit.available.amount > 0 ? (
                <Stack spacing={0.5} alignItems="flex-start">
                  <Button size="small" disabled data-testid="bil-credit-refund">
                    {t('bil:finance.refund')}
                  </Button>
                  <Typography variant="caption" color="text.secondary" data-testid="bil-refund-offline">
                    {t('bil:offline.refund_disabled')}
                  </Typography>
                </Stack>
              ) : null}
              {canRefund && credit.available.amount > 0 ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    onClick={() => {
                      setRefund({ amount: String(credit.available.amount), method: '', iban: '', reason: '', reference: '' });
                      setRefunding(credit);
                    }}
                    data-testid="bil-credit-refund"
                  >
                    {t('bil:finance.refund')}
                  </Button>
                  <Button size="small" onClick={() => toWallet.mutate(credit)} data-testid="bil-credit-to-wallet">
                    {t('bil:finance.to_wallet')}
                  </Button>
                </Stack>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </Section>
      {toWallet.error ? <ErrorState error={toWallet.error} compact /> : null}

      <Section title={t('bil:finance.wallet_ledger')}>
        {data.walletTransactions.map((row) => (
          <TableRow key={row.id} data-testid="bil-wallet-row" data-direction={row.direction}>
            <TableCell>
              <JalaliDate value={row.occurredAt} withTime />
            </TableCell>
            <TableCell>{t(`bil:wallet_direction.${row.direction}`)}</TableCell>
            <TableCell>{t(`bil:wallet_bucket.${row.bucket}`)}</TableCell>
            <TableCell>
              <Amount money={row.amount} />
            </TableCell>
            <TableCell>{t(`bil:wallet_source.${row.source}`, { defaultValue: row.source })}</TableCell>
          </TableRow>
        ))}
      </Section>

      <Section title={t('bil:finance.receivables')}>
        {data.receivables.map((row) => (
          <TableRow key={row.id}>
            <TableCell>
              <JalaliDate value={row.createdAt} />
            </TableCell>
            <TableCell>{t(`bil:receivable_source.${row.source}`)}</TableCell>
            <TableCell>{t(`bil:receivable_status.${row.status}`)}</TableCell>
            <TableCell>
              <Amount money={row.outstanding} />
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
      </Section>

      <Section title={t('bil:deposits.title')}>
        {data.deposits.map((row) => (
          <TableRow key={row.id}>
            <TableCell>
              <JalaliDate value={row.requestedAt} />
            </TableCell>
            <TableCell>
              <DepositStatusChip status={row.status} />
            </TableCell>
            <TableCell />
            <TableCell>
              <Amount money={row.amount} />
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
      </Section>

      <Section title={t('bil:refunds.title')}>
        {data.refunds.map((row) => (
          <TableRow key={row.id}>
            <TableCell>
              <JalaliDate value={row.requestedAt} />
            </TableCell>
            <TableCell>{t(`bil:method.${row.method}`)}</TableCell>
            <TableCell>
              <RefundStatusChip status={row.status} />
            </TableCell>
            <TableCell>
              <Amount money={row.amount} />
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
      </Section>

      <Section title={t('bil:write_off.history')}>
        {(writeOffs.data?.items ?? []).map((row) => (
          <TableRow key={row.id} data-testid="bil-write-off-row">
            <TableCell>
              <JalaliDate value={row.businessDate} />
            </TableCell>
            <TableCell>{t('bil:write_off.title')}</TableCell>
            <TableCell>{row.reason}</TableCell>
            <TableCell>
              <Amount money={row.amount} />
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
      </Section>

      {invoiceAction?.kind === 'plan' ? <InstallmentPlanDialog open invoiceId={invoiceAction.invoice.id} onClose={() => setInvoiceAction(null)} /> : null}
      {invoiceAction?.kind === 'cheque' ? (
        <ChequeDialog open patientId={patientId} amount={invoiceAction.invoice.balance.amount} invoiceIds={[invoiceAction.invoice.id]} onClose={() => setInvoiceAction(null)} />
      ) : null}
      {invoiceAction?.kind === 'write-off' ? <WriteOffDialog open patientId={patientId} invoiceIds={[invoiceAction.invoice.id]} onClose={() => setInvoiceAction(null)} /> : null}

      <FormDialog
        open={refunding !== null}
        titleKey="bil:finance.refund"
        onSubmit={() => refunding && createRefund.mutate(refunding)}
        onClose={() => setRefunding(null)}
        isDirty
        isSubmitting={createRefund.isPending}
        submitError={createRefund.error}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label={t('bil:cashier.amount')} value={refund.amount} onChange={(event) => setRefund({ ...refund, amount: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-refund-amount' } }} />
          <TextField
            select
            label={t('bil:refunds.columns.method')}
            value={refund.method}
            onChange={(event) => setRefund({ ...refund, method: event.target.value as BilPaymentMethod })}
            helperText={t('bil:finance.method_default')}
            slotProps={{ htmlInput: { 'data-testid': 'bil-refund-method' } }}
          >
            <MenuItem value="">{t('bil:finance.original_method')}</MenuItem>
            {REFUND_METHODS.map((method) => (
              <MenuItem key={method} value={method}>
                {t(`bil:method.${method}`)}
              </MenuItem>
            ))}
          </TextField>
          {refund.method === 'BankTransfer' ? (
            <TextField label={t('bil:finance.iban')} value={refund.iban} onChange={(event) => setRefund({ ...refund, iban: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
          ) : null}
          <TextField label={t('bil:finance.reason')} value={refund.reason} onChange={(event) => setRefund({ ...refund, reason: event.target.value })} />
        </Stack>
      </FormDialog>

      <FormDialog
        open={topUpOpen}
        titleKey="bil:finance.top_up"
        onSubmit={() => doTopUp.mutate()}
        onClose={() => setTopUpOpen(false)}
        isDirty={topUp.amount !== ''}
        isSubmitting={doTopUp.isPending}
        submitError={doTopUp.error}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label={t('bil:cashier.amount')} value={topUp.amount} onChange={(event) => setTopUp({ ...topUp, amount: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-topup-amount' } }} />
          <TextField select label={t('bil:refunds.columns.method')} value={topUp.method} onChange={(event) => setTopUp({ ...topUp, method: event.target.value as BilPaymentMethod })}>
            {TOPUP_METHODS.map((method) => (
              <MenuItem key={method} value={method}>
                {t(`bil:method.${method}`)}
              </MenuItem>
            ))}
          </TextField>
          {needsReference(topUp.method) ? (
            <TextField required label={t('bil:cashier.pos_reference')} value={topUp.reference} onChange={(event) => setTopUp({ ...topUp, reference: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
          ) : null}
        </Stack>
      </FormDialog>
      {approvalDialog}
    </Stack>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode[] }) {
  const { t } = useTranslation('bil');
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      {children.length === 0 ? (
        <Typography color="text.secondary">{t('bil:finance.none')}</Typography>
      ) : (
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>{t('bil:finance.col_date')}</TableCell>
              <TableCell>{t('bil:finance.col_kind')}</TableCell>
              <TableCell>{t('bil:finance.col_detail')}</TableCell>
              <TableCell>{t('bil:finance.col_amount')}</TableCell>
              <TableCell>{t('bil:finance.col_actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      )}
    </Paper>
  );
}
